import { PUT_GLOBAL_VISA_VACCINE_FAILURE } from 'modules/authorizations/store/types/globalTypes';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { updateGlobalVisaAndVaccine } from 'modules/authorizations/store/actions/global.actions';
import { showToast } from 'modules/layout/layout.actions';
import EditableCard from 'modules/common/editableCard.component';
import React, { useState } from 'react';

import { getGlobalInfo } from 'modules/authorizations/authorizations.actions';
import { toDateOrNull, toGuidOrZero, toNumberOrNull, toStringOrEmpty } from './visaTab.component';
import { withDateChronology } from 'utilities/dateChronology';

const PASSPORT_DATE_PAIRS = [['passportReceivedDate', 'passportExpiredDate']];

const VisaPermitCard = props => {
  const [isLoading, setIsLoading] = useState(false);

  const { globalInfo, authId, isReadOnly, globalId, familyMemberId, enumerations } = props;

  const visaTypeOptions =
    enumerations && enumerations.visaTypes && enumerations.visaTypes.length > 0
      ? enumerations.visaTypes.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const visaStatusOptions =
    enumerations && enumerations.visaStatuses && enumerations.visaStatuses.length > 0
      ? enumerations.visaStatuses.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const countries =
    enumerations && enumerations.countries && enumerations.countries.length > 0
      ? enumerations.countries.filter(s => s.isActive === true).sort((a, b) => {
          const usaTitles = ['United States of America', 'USA', 'United States', 'US'];
          const isAUsa = usaTitles.includes(a.title);
          const isBUsa = usaTitles.includes(b.title);
          if (isAUsa !== isBUsa) return isAUsa ? -1 : 1;
          return a.title.localeCompare(b.title);
        })
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const tabs = [{ header: 'Visa/Immigration', order: 1, iconPath: ['fas', 'passport'] }];

  const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

  const summaries = [
    {
      header: 'Visa Status',
      accessorKey: 'visaStatusId',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.visaStatusId ?? ZERO_GUID,
      options: visaStatusOptions,
      tab: 1
    },
    {
      header: 'Current Visa',
      accessorKey: 'currentVisaId',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.currentVisaId ?? ZERO_GUID,
      options: visaTypeOptions,
      tab: 1
    },
    {
      header: 'Visa Expiry Date',
      accessorKey: 'visaExpiredDate',
      type: 'date',
      required: false,
      value: globalInfo?.visaExpiredDate ?? '',
      tab: 1
    },
    {
      header: 'Visa Max Date',
      accessorKey: 'visaMaxDate',
      type: 'date',
      required: false,
      value: globalInfo?.visaMaxDate ?? '',
      tab: 1
    },
    {
      header: 'Visa Issued By',
      accessorKey: 'visaIssuedById',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.visaIssuedById ?? ZERO_GUID,
      options: countries,
      tab: 1
    },
    {
      header: 'Visa Document Number',
      accessorKey: 'visaDocumentNumber',
      type: 'text',
      required: false,
      value: globalInfo?.visaDocumentNumber ?? '',
      tab: 1
    },
    {
      header: 'Interview Date',
      accessorKey: 'visaInterviewDate',
      type: 'date',
      required: false,
      value: globalInfo?.visaInterviewDate ?? '',
      tab: 1
    },
    {
      header: 'Consulate',
      accessorKey: 'visaConsulate',
      type: 'text',
      required: false,
      value: globalInfo?.visaConsulate ?? '',
      tab: 1
    },
    {
      header: 'Receipt Number',
      accessorKey: 'visaReceiptNumber',
      type: 'text',
      required: false,
      value: globalInfo?.visaReceiptNumber ?? '',
      tab: 1
    },
    {
      header: 'Travel To Date',
      accessorKey: 'visaTravelToDate',
      type: 'date',
      required: false,
      value: globalInfo?.visaTravelToDate ?? '',
      tab: 1
    },
    { dividerLine: true, tab: 1 },
    {
      header: 'Passport Number',
      accessorKey: 'passportNumber',
      type: 'text',
      required: false,
      value: globalInfo?.passportNumber ?? '',
      tab: 1
    },
    {
      header: 'Passport Received',
      accessorKey: 'passportReceivedDate',
      type: 'date',
      required: false,
      value: globalInfo?.passportReceivedDate ?? '',
      tab: 1
    },
    {
      header: 'Passport Expiry',
      accessorKey: 'passportExpiredDate',
      type: 'date',
      required: false,
      value: globalInfo?.passportExpiredDate ?? '',
      tab: 1
    }
  ];

  const updateFields = async (tab, values) => {
    setIsLoading(true);

    const dto = {
      ...globalInfo,
      familyMemberId: toNumberOrNull(familyMemberId),
      currentVisaId: toGuidOrZero(values.currentVisaId),
      visaExpiredDate: toDateOrNull(values.visaExpiredDate),
      visaMaxDate: toDateOrNull(values.visaMaxDate),
      visaIssuedById: toGuidOrZero(values.visaIssuedById),
      visaDocumentNumber: toStringOrEmpty(values.visaDocumentNumber),
      visaStatusId: toGuidOrZero(values.visaStatusId),
      visaInterviewDate: toDateOrNull(values.visaInterviewDate),
      visaConsulate: toStringOrEmpty(values.visaConsulate),
      visaReceiptNumber: toStringOrEmpty(values.visaReceiptNumber),
      visaTravelToDate: toDateOrNull(values.visaTravelToDate),
      passportNumber: toStringOrEmpty(values.passportNumber),
      passportReceivedDate: toDateOrNull(values.passportReceivedDate),
      passportExpiredDate: toDateOrNull(values.passportExpiredDate)
    };

    const resp = await props.updateGlobalVisaAndVaccine(authId, globalId, familyMemberId, dto);

    if (resp.type === PUT_GLOBAL_VISA_VACCINE_FAILURE) {
      props.showToast('Failed to update visa and vaccination info, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return false;
    } else {
      await props.getGlobalInfo(authId);
    }

    setIsLoading(false);
    return true;
  };

  return (
    <EditableCard
      tabs={tabs}
      summaries={withDateChronology(summaries, PASSPORT_DATE_PAIRS)}
      numColumns={5}
      updateFields={updateFields}
      isLoading={isLoading}
      hideEdit={isReadOnly}
    />
  );
};

export default compose(
  connect(null, {
    updateGlobalVisaAndVaccine,
    getGlobalInfo,
    showToast
  })
)(VisaPermitCard);
